import { SceneData } from './SceneExporter';
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter.js';
import { RepaintProgress } from '../Gui/components/panels/ProgressBar';

/**
 * Export3DStl.ts
 *
 * Goal:
 * - Export SceneData to STL (binary).
 *
 * Notes:
 * - Skip the meshes without geometry or positions.
 */

export async function Export3DStl(
    scenedata: SceneData,
    setProgress: (progress: number | undefined) => void,
    orientation3d: 'Y_Up' | 'Z_Up' = 'Y_Up'
): Promise<{ formattedData: Uint8Array; extension?: string }> {
    console.log('Exporting scene to OBJ format');

    const exporter = new STLExporter();
    const sceneToExport: THREE.Scene = new THREE.Scene();
    sceneToExport.name = scenedata.geoDataName || 'ExportedScene';

    const stlAxisFix = orientation3d === 'Z_Up' ? new THREE.Matrix4().makeRotationX(Math.PI / 2) : new THREE.Matrix4().identity();

    // Helper function to check if an object is a valid mesh for STL export
    const isValidMeshForStl = (obj: THREE.Object3D): obj is THREE.Mesh => {
        const geom = (obj as any).geometry as THREE.BufferGeometry | undefined;
        if (!geom || geom.isBufferGeometry !== true) return false;

        const pos = geom.getAttribute?.('position') as THREE.BufferAttribute | undefined;
        return !!pos && typeof pos.count === 'number' && pos.count > 0;
    };

    let progress = 0;
    const totalProgress = scenedata.layerMeshes.length;
    setProgress((progress / totalProgress) * 80 + 10);

    for (let i = 0; i < scenedata.layerMeshes.length; i++) {
        const group = new THREE.Group();
        group.name = scenedata.layerGroupNames[i] || `Group_${i}`;

        for (const root of scenedata.layerMeshes[i]) {
            root.traverse((obj) => {
                if (!isValidMeshForStl(obj)) return;

                const mesh = obj as THREE.Mesh;
                const cloned = mesh.clone(false) as THREE.Mesh;
                cloned.geometry = (mesh.geometry as THREE.BufferGeometry).clone();

                cloned.geometry.applyMatrix4(stlAxisFix);

                cloned.position.set(0, 0, 0);
                cloned.rotation.set(0, 0, 0);
                cloned.scale.set(1, 1, 1);
                cloned.updateMatrix();
                cloned.updateMatrixWorld(true);

                group.add(cloned);
            });
        }

        sceneToExport.add(group);

        progress++;
        setProgress((progress / totalProgress) * 80 + 10);
        await RepaintProgress();
    }

    sceneToExport.updateMatrixWorld(true);

    const dv = exporter.parse(sceneToExport, { binary: true }) as DataView;
    return { formattedData: new Uint8Array(dv.buffer), extension: 'stl' };
}
